
/* ==========================================================================
   BruBag — Custom Tabs
   ========================================================================== */

(() => {
  if (customElements.get('custom-tabs')) return;

  class CustomTabs extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;

      this._items = this.querySelector(
        ':scope > .brubag-tabs__items'
      );

      if (!this._items) return;

      this._connected = true;

      this._onClick = this.handleClick.bind(this);
      this._onKeydown = this.handleKeydown.bind(this);
      this._onBlockSelect = this.handleBlockSelect.bind(this);
      this._onResize = this.updateOrientation.bind(this);

      this._items.addEventListener('click', this._onClick);
      this._items.addEventListener('keydown', this._onKeydown);

      document.addEventListener(
        'shopify:block:select',
        this._onBlockSelect
      );

      this._mobileQuery = window.matchMedia('(max-width: 749px)');

      this._mobileQuery.addEventListener(
        'change',
        this._onResize
      );

      this._observer = new MutationObserver(() => {
        this.refresh();
      });

      this._observer.observe(this._items, {
        childList: true,
        subtree: true,
        characterData: true
      });

      this.refresh();
    }

    disconnectedCallback() {
      if (!this._connected) return;

      this._items.removeEventListener('click', this._onClick);
      this._items.removeEventListener('keydown', this._onKeydown);

      document.removeEventListener(
        'shopify:block:select',
        this._onBlockSelect
      );

      this._mobileQuery?.removeEventListener(
        'change',
        this._onResize
      );

      this._observer?.disconnect();
      this._connected = false;
    }

    /* Discover Native Tab Blocks
       ====================================================================== */

    refresh() {
      const previous = this._activePair;

      this._pairs = Array.from(this._items.children).filter(
        (element) => element.classList.contains('brubag-tabs__item')
      );

      this._triggers = [];
      this._panels = [];

      this.style.setProperty(
        '--brubag-tabs-count',
        String(this._pairs.length)
      );

      this._pairs.forEach((pair, index) => {
        const trigger = pair.querySelector(
          ':scope > .brubag-tabs__trigger'
        );

        const panel = pair.querySelector(
          ':scope > .brubag-tabs__panel'
        );

        if (!trigger || !panel) return;

        const identifier = `${this.id}-${index + 1}`;

        trigger.id = `${identifier}-tab`;
        panel.id = `${identifier}-panel`;

        trigger.setAttribute('role', 'tab');
        trigger.setAttribute('aria-controls', panel.id);

        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', trigger.id);

        const accessibleName =
          pair.dataset.accessibleName?.trim() ||
          trigger.textContent.trim().replace(/\s+/g, ' ') ||
          `Tab ${index + 1}`;

        trigger.setAttribute('aria-label', accessibleName);

        this._triggers.push(trigger);
        this._panels.push(panel);
      });

      const previousIndex = this._pairs.indexOf(previous);

      this.activate(
        previousIndex >= 0 ? previousIndex : 0
      );

      this.updateOrientation();
      this.setAttribute('data-ready', '');
    }

    /* Activate a Tab
       ====================================================================== */

    activate(index, focus = false) {
      if (index < 0 || index >= this._pairs.length) return;

      this._activePair = this._pairs[index];

      this._pairs.forEach((pair, position) => {
        const active = position === index;
        const trigger = this._triggers[position];
        const panel = this._panels[position];

        if (!trigger || !panel) return;

        trigger.setAttribute('aria-selected', String(active));
        trigger.tabIndex = active ? 0 : -1;

        panel.hidden = !active;
      });

      if (focus) {
        this._triggers[index]?.focus({
          preventScroll: true
        });
      }
    }

    /* Pointer Interaction
       ====================================================================== */

    handleClick(event) {
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest(
        '.brubag-tabs__trigger'
      );

      if (!trigger || !this._items.contains(trigger)) return;

      /* Let nested interactive native elements
         handle their own interactions. */

      const interactive = event.target.closest(
        'a, button, input, select, textarea, video, audio, summary'
      );

      if (interactive && interactive !== trigger) return;

      const index = this._triggers.indexOf(trigger);

      if (index >= 0) this.activate(index);
    }

    /* Keyboard Interaction
       ====================================================================== */

    handleKeydown(event) {
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest(
        '.brubag-tabs__trigger'
      );

      if (!trigger || event.target !== trigger) return;

      const current = this._triggers.indexOf(trigger);
      const count = this._triggers.length;

      if (current < 0 || !count) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.activate(current);
        return;
      }

      const vertical =
        this._items.getAttribute('aria-orientation') === 'vertical';

      const rtl = document.documentElement.dir === 'rtl';

      let next = null;

      switch (event.key) {
        case 'ArrowRight':
          if (!vertical) next = current + (rtl ? -1 : 1);
          break;

        case 'ArrowLeft':
          if (!vertical) next = current + (rtl ? 1 : -1);
          break;

        case 'ArrowDown':
          if (vertical) next = current + 1;
          break;

        case 'ArrowUp':
          if (vertical) next = current - 1;
          break;

        case 'Home':
          next = 0;
          break;

        case 'End':
          next = count - 1;
          break;
      }

      if (next === null) return;

      event.preventDefault();

      this.activate((next + count) % count, true);
    }

    /* Responsive Navigation
       ====================================================================== */

    updateOrientation() {
      const layout = this._mobileQuery?.matches
        ? this.dataset.mobileLayout
        : this.dataset.layout;

      this._items.setAttribute(
        'aria-orientation',
        layout === 'left' ? 'vertical' : 'horizontal'
      );
    }

    /* Shopify Theme Editor
       ====================================================================== */

    handleBlockSelect(event) {
      if (!(event.target instanceof Element)) return;

      if (!this.contains(event.target)) return;

      const pair = event.target.closest(
        '.brubag-tabs__item'
      );

      if (!pair) return;

      const index = this._pairs.indexOf(pair);

      if (index >= 0) {
        this.activate(index);
      }
    }
  }

  customElements.define('custom-tabs', CustomTabs);
})();
